import React, { useEffect, useState } from "react";
import { getCounterfactual, DssApiError } from "../../api/dss.js";
import { formatScore, NO_SESSION_KEY } from "../../decision/importColumns.mjs";

const LABELS = { ses: "Сесія", dorm: "Гуртожиток", act: "Діяльність", zone: "Віддаленість" };
const CODES = ["ses", "dorm", "act", "zone"];
// Віддаленість q визначається місцем проживання: здобувач її не змінює, тож
// приросту балів за нею немає і до порад вона не потрапляє.
const FIXED_CODES = new Set(["zone"]);
const isFixed = (code) => FIXED_CODES.has(code);
const FIXED_NOTE = "не змінюється здобувачем";
// Звичайна сума R = s + g + f не містить q, тож неявна вага q у ній 0.
const SUM_CODES = new Set(["ses", "dorm", "act"]);
// Запас над прохідним рівнем, менший за цей, показується як "мінімальний".
const THIN_MARGIN = 0.01;
// Різниця ваг (модель проти суми балів), меншу за цю, вважаємо збігом.
const WEIGHT_EPS = 0.005;

const label = (code) => LABELS[code] || code;
const pct = (value) => `${Math.round(value * 100)}%`;

function formatDelta(item) {
  if (!item.reachable) return "недосяжно — шкалу вичерпано";
  if (item.delta === 0) return "0 балів (вже достатньо)";
  return `+${item.delta.toFixed(2)} бала (з ${item.value} до ${(item.value + item.delta).toFixed(2)} із ${item.max})`;
}

// Частка шкали, яку студент уже набрав за критерієм: (x - min) / (max - min).
function scaleShare(item) {
  const low = item.min ?? 0;
  const range = item.max - low;
  return range > 0 ? (item.value - low) / range : 0;
}

// Неявна вага критерію в "сумі балів": кожен бал важить однаково, тож
// критерій важить стільки, скільки балів у його шкалі.
function sumWeights(items) {
  const inSum = items.filter((i) => SUM_CODES.has(i.code));
  const total = inSum.reduce((acc, i) => acc + (i.max - (i.min ?? 0)), 0);
  return Object.fromEntries(
    items.map((i) => [
      i.code,
      SUM_CODES.has(i.code) && total > 0 ? (i.max - (i.min ?? 0)) / total : 0,
    ])
  );
}

// Якісне пояснення різниці між рангом за сумою і рангом за моделлю:
// (w_j - s_j) * r_j -- наскільки критерій j важить більше/менше в моделі,
// ніж у сумі, помножене на те, наскільки студент сильний за ним. Це не
// точне розкладання TOPSIS, а напрямок впливу ваг.
function positionReason(row, items) {
  if (typeof row.positionChange !== "number" || row.positionChange === 0 || items.length === 0) return null;
  if (items.some((i) => typeof i.weight !== "number")) return null;
  const implicit = sumWeights(items);
  const shift =
    row.positionChange > 0
      ? `вище, ніж за сумою (+${row.positionChange})`
      : `нижче, ніж за сумою (${row.positionChange})`;
  if (items.every((i) => Math.abs(i.weight - implicit[i.code]) < WEIGHT_EPS)) {
    return `Чому ${shift}: ваги моделі тут збігаються з вагою балів у сумі, тож зсув дає сама будова TOPSIS — показник C рахує відстані до ідеального й антиідеального розв'язків, тому однакова сума з різним розподілом балів між критеріями дає різний C.`;
  }
  const effects = items.map((i) => ({
    ...i,
    implicit: implicit[i.code],
    effect: (i.weight - implicit[i.code]) * scaleShare(i),
  }));
  const up = row.positionChange > 0;
  const driver = [...effects].sort((a, b) => (up ? b.effect - a.effect : a.effect - b.effect))[0];
  const heavier = driver.weight > driver.implicit;
  const strength = pct(scaleShare(driver));
  const weights = `${pct(driver.weight)} у моделі проти ${pct(driver.implicit)} у сумі балів`;
  if (up) {
    return heavier
      ? `Чому ${shift}: «${label(driver.code)}» важить ${weights}, а в студента тут ${strength} шкали.`
      : `Чому ${shift}: слабке місце студента — «${label(driver.code)}» (${strength} шкали), а модель цінує цей критерій менше (${weights}), тож воно шкодить менше.`;
  }
  return heavier
    ? `Чому ${shift}: «${label(driver.code)}» важить ${weights}, а в студента тут лише ${strength} шкали.`
    : `Чому ${shift}: бали студента зосереджені в «${label(driver.code)}» (${strength} шкали), а модель цінує цей критерій менше (${weights}).`;
}

const Bar = ({ value, color, testId }) => (
  <div className="explain-bar" data-testid={testId}>
    <div
      className="explain-bar-fill"
      style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%`, background: color }}
    />
  </div>
);

// Шкала C від 0 до 1 з позначками C студента і прохідного C*.
const ScoreScale = ({ score, cutoff, passes }) => (
  <div className="explain-scale" aria-label="Показник C і прохідний C*">
    <div className="explain-scale-track" />
    {typeof cutoff === "number" && (
      <div className="explain-scale-marker explain-scale-cutoff" style={{ left: `${cutoff * 100}%` }}>
        <span>C*</span>
      </div>
    )}
    {typeof score === "number" && (
      <div
        className="explain-scale-marker explain-scale-score"
        style={{ left: `${score * 100}%`, background: passes ? "var(--free-room)" : "var(--full-busy)" }}
      >
        <span>C</span>
      </div>
    )}
  </div>
);

function byCriterionOrder(a, b) {
  const ia = CODES.indexOf(a.code);
  const ib = CODES.indexOf(b.code);
  return (ia === -1 ? CODES.length : ia) - (ib === -1 ? CODES.length : ib);
}

// Усе, що картка виводить із рядка звіту й відповіді DSS, -- окремо від
// розмітки, щоб компонент лишався простим.
function deriveExplanation(row, cutoff, data) {
  const ordered = [...(data?.items || [])].sort(byCriterionOrder);
  const hasScores = typeof row.score === "number" && typeof cutoff === "number";
  return {
    ordered,
    loses: row.modelStatus === "loses",
    noSession: row.groupKey === NO_SESSION_KEY,
    priority: typeof row.groupKey === "string" && row.groupKey.startsWith("priority_"),
    passes: hasScores && row.score >= cutoff,
    margin: hasScores ? row.score - cutoff : null,
    mainGap: [...ordered].sort((a, b) => b.phi - a.phi)[0],
    bestLever: ordered.filter((i) => !isFixed(i.code) && (i.gainPerPoint ?? 0) > 0).sort((a, b) => b.gainPerPoint - a.gainPerPoint)[0],
    cheapest: ordered.filter((i) => !isFixed(i.code) && i.reachable && i.delta > 0).sort((a, b) => a.delta - b.delta)[0],
    reason: positionReason(row, ordered),
  };
}

// Картка "Чому так і що робити" для одного студента (клік по рядку звіту).
// До DSS іде лише {ses, dorm, act, zone} і C* -- без ПІБ, id і кімнати. Детальне
// пояснення (формули 13 і 16) визначене лише для абсолютного режиму.
const CounterfactualPanel = ({ row, cutoff, normalization, onClose }) => {
  const [state, setState] = useState({ status: "idle", data: null, message: "" });
  const absolute = normalization === "absolute";

  useEffect(() => {
    if (!absolute || typeof cutoff !== "number") return undefined;
    let cancelled = false;
    setState({ status: "loading", data: null, message: "" });
    const { ses, dorm, act, zone } = row.values;
    getCounterfactual({ values: { ses, dorm, act, zone }, cutoff, normalization })
      .then((data) => {
        if (!cancelled) setState({ status: "done", data: data || {}, message: "" });
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof DssApiError ? `Помилка (${err.status}): ${err.message}` : "Не вдалося отримати пояснення.";
        setState({ status: "error", data: null, message });
      });
    return () => {
      cancelled = true;
    };
  }, [row.id, row.values, cutoff, normalization, absolute]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { ordered, loses, noSession, priority, passes, margin, mainGap, bestLever, cheapest, reason } =
    deriveExplanation(row, cutoff, state.data);
  const change = row.positionChange;
  const title = row.pib || "#" + row.id;

  return (
    <div className="explain-overlay">
      {/* Клік поза карткою закриває її: фон -- справжня кнопка, а не div з onClick. */}
      <button type="button" className="explain-backdrop" aria-label="Закрити пояснення" onClick={onClose} />
      <dialog
        open
        className="explain-modal"
        aria-modal="true"
        aria-label={`Пояснення: ${title}`}
        data-testid="counterfactual-panel"
      >
        <div className="explain-head">
          <div>
            <h3>{title}</h3>
            <span className={`explain-badge ${loses ? "explain-badge-loses" : "explain-badge-retains"}`}>
              {loses ? "втрачає місце" : "зберігає місце"}
            </span>
          </div>
          <button type="button" className="restore-button" style={{ marginLeft: "auto" }} onClick={onClose}>
            Закрити
          </button>
        </div>
        <div className="explain-body">
          <section className="input-component explain-section">
            <h3 className="text-header">Підсумок</h3>
            <div className="explain-stats">
              <div className="explain-stat">
                <span>Ранг за сумою</span>
                <strong>{row.sumRank ?? "—"}</strong>
              </div>
              <div className="explain-stat">
                <span>Ранг за моделлю</span>
                <strong>{row.modelRank ?? "—"}</strong>
              </div>
              <div className="explain-stat">
                <span>Зміна позиції</span>
                <strong>{typeof change === "number" && change > 0 ? `+${change}` : (change ?? "—")}</strong>
              </div>
            </div>
            <ScoreScale score={row.score} cutoff={cutoff} passes={passes} />
            <p className="text-header">
              Показник C = {formatScore(row.score)}, прохідний C* = {formatScore(cutoff)}
              {margin !== null && ` (${margin >= 0 ? "запас" : "бракує"} ${Math.abs(margin).toFixed(4)})`}.
            </p>
            {noSession && (
              <p className="explain-note">
                Немає балів за сесію: студент ранжується в окремій групі, яка стоїть у черзі після загального конкурсу.
                Найдієвіший крок — отримати бали за сесію.
              </p>
            )}
            {priority && (
              <p className="explain-note">
                Пільгова категорія — жорстке обмеження: студент стоїть у черзі своєї пільгової групи незалежно від
                балів.
              </p>
            )}
          </section>

          {!absolute && (
            <p className="text-header">
              Пояснення визначене лише для абсолютного режиму нормалізації: у класичному показник C залежить від усіх
              інших претендентів.
            </p>
          )}
          {absolute && state.status === "loading" && <p className="text-header">Розрахунок...</p>}
          {state.status === "error" && (
            <p className="text-header" style={{ color: "var(--full-busy)" }}>
              {state.message}
            </p>
          )}

          {state.status === "done" && (
            <>
              <section className="input-component explain-section">
                <h3 className="text-header">Чому так</h3>
                <table className="table explain-table">
                  <thead>
                    <tr>
                      <th className="table-header">Критерій</th>
                      <th className="table-header">Бали (частка шкали)</th>
                      <th className="table-header">Вага в моделі</th>
                      <th className="table-header">Частка у відставанні від ідеалу</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordered.map((item) => (
                      <tr key={item.code}>
                        <td className="table-content">{label(item.code)}</td>
                        <td className="table-content">
                          {item.value} / {item.max}
                          <Bar value={scaleShare(item)} color="var(--main-color-1)" />
                        </td>
                        <td className="table-content">{typeof item.weight === "number" ? pct(item.weight) : "—"}</td>
                        <td className="table-content">
                          {pct(item.phi)}
                          <Bar value={item.phi} color="var(--half-busy)" testId={`phi-${item.code}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {mainGap && mainGap.phi > 0 && (
                  <p className="text-header">
                    Найбільше від ідеального розв'язку студента відділяє «{label(mainGap.code)}»: {pct(mainGap.phi)}{" "}
                    усього відставання ({mainGap.value} із {mainGap.max}).
                  </p>
                )}
                {reason && <p className="text-header">{reason}</p>}
              </section>

              <section className="input-component explain-section">
                <h3 className="text-header">{loses ? "Скільки бракує до прохідного рівня" : "Що можна зробити"}</h3>
                {loses ? (
                  <>
                    <table className="table explain-table">
                      <thead>
                        <tr>
                          <th className="table-header">Показник</th>
                          <th className="table-header">Поточний бал</th>
                          <th className="table-header">Бракує</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordered.map((item) => (
                          <tr
                            key={item.code}
                            className={cheapest && cheapest.code === item.code ? "explain-best" : undefined}
                          >
                            <td className="table-content">{label(item.code)}</td>
                            <td className="table-content">
                              {item.value} / {item.max}
                            </td>
                            <td className="table-content">{isFixed(item.code) ? FIXED_NOTE : formatDelta(item)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {cheapest ? (
                      <p className="text-header">
                        Найменше балів треба за критерієм «{label(cheapest.code)}»: +{cheapest.delta.toFixed(2)}. Кожен
                        варіант рахується окремо (решта балів без змін); покращення за кількома критеріями разом
                        потребує менше балів у кожному.
                      </p>
                    ) : (
                      <p className="text-header">
                        Жоден критерій окремо не доводить до прохідного рівня — потрібне покращення одразу за кількома.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-header">
                    Студент проходить
                    {margin !== null && margin < THIN_MARGIN
                      ? ", але запас мінімальний — будь-яке зниження балів може коштувати місця"
                      : ""}
                    .
                  </p>
                )}
                {bestLever && (
                  <p className="text-header">
                    Найвигідніше підсилювати «{label(bestLever.code)}»: +1 бал дає +{bestLever.gainPerPoint.toFixed(4)}{" "}
                    до C.
                  </p>
                )}
              </section>
            </>
          )}
        </div>
      </dialog>
    </div>
  );
};

export default CounterfactualPanel;
